import { Controller } from 'stimulus';

// My week (my_weeks/show): drag a task's card to another day to lock it there,
// through the Schedule's own drag (SchedulesController#plan_task, op move).
// The page reloads with the change note and its Undo (plan_undo), which carry
// over the reload in session storage for a minute at most.
export default class extends Controller {
  static targets = ['note'];

  connect() {
    if (document.documentElement.hasAttribute('data-turbolinks-preview')) return;
    let saved = null;
    try { saved = JSON.parse(window.sessionStorage.getItem(this.data.get('key')) || 'null'); window.sessionStorage.removeItem(this.data.get('key')); } catch (e) { saved = null; }
    if (saved && Date.now() - (saved.at || 0) < 60000) this.showNote(saved.text, saved.undo);
  }

  // The Key opened or closed: saved for the person (not when the page opens it as saved)
  keyToggled(e) {
    const key = e.currentTarget;
    if (String(key.open) === key.dataset.saved) return;
    key.dataset.saved = String(key.open);
    const meta = document.querySelector('meta[name="csrf-token"]');
    fetch(this.data.get('keyUrl'), {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': meta ? meta.content : '', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ open: key.open })
    }).catch(() => {});
  }

  start(e) {
    const card = e.currentTarget;
    this.dragging = card;
    card.classList.add('mw-card--dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', card.dataset.task);
  }

  end() {
    if (this.dragging) this.dragging.classList.remove('mw-card--dragging');
    this.element.querySelectorAll('.mw-col--over').forEach((c) => c.classList.remove('mw-col--over'));
  }

  over(e) {
    if (!this.dragging) return;
    e.preventDefault();
    e.currentTarget.classList.add('mw-col--over');
  }

  leave(e) {
    e.currentTarget.classList.remove('mw-col--over');
  }

  drop(e) {
    e.preventDefault();
    const col = e.currentTarget;
    col.classList.remove('mw-col--over');
    const card = this.dragging;
    this.dragging = null;
    if (card) card.classList.remove('mw-card--dragging');
    if (!card || card.closest('.mw-col') === col) return;
    this.request(card.dataset.url, 'PATCH', { op: 'move', start_on: col.dataset.day })
      .then((json) => this.reloadWith([...(json.changes || []), ...((json.plan && json.plan.lines) || [])].join(' '), json.undo))
      .catch((err) => this.showNote(err.message, null, true));
  }

  undo(e) {
    e.preventDefault();
    if (!this.undoItems) return;
    const items = this.undoItems;
    this.undoItems = null;
    this.request(this.data.get('undoUrl'), 'POST', { undo: items })
      .then(() => this.reloadWith('Undone.', null))
      .catch((err) => this.showNote(err.message, null, true));
  }

  request(url, method, body) {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': meta ? meta.content : '', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        if (!json.ok) throw new Error(json.error || 'That could not be saved.');
        return json;
      });
  }

  reloadWith(text, undo) {
    try { window.sessionStorage.setItem(this.data.get('key'), JSON.stringify({ text, undo, at: Date.now() })); } catch (e) { /* the note is a convenience */ }
    if (window.Turbolinks) window.Turbolinks.visit(window.location.href, { action: 'replace' });
    else window.location.reload();
  }

  showNote(text, undo, error) {
    if (!this.hasNoteTarget) return;
    this.undoItems = undo && undo.length ? undo : null;
    const body = error ? `<span class="mw-err">${this.escape(text)}</span>` : this.escape(text);
    this.noteTarget.innerHTML = body + (this.undoItems ? ' <button type="button" class="link focus:outline-none" data-action="my-week#undo">Undo</button>' : '');
    this.noteTarget.classList.add('mw-note--change');
  }

  escape(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
